/**
 * PendingApprovalsWidget — FASE 4 / M3 (auditoria 2026-05-21).
 *
 * Mostra aprovações de tarefa pendentes pro admin atual. Cada item tem
 * botões inline [✓ Aprovar][✕ Rejeitar] reusando TaskApprovalActions
 * (UI-2 / TD-V2-PERM-06).
 *
 * Renderizado opcionalmente:
 *   - Em HomePage (widget rápido)
 *   - Em /perfil (futuro)
 *
 * Auto-hide quando lista vazia — não polui Home pra users sem pendências.
 */

import { Card, Alert } from '@/components/ui';
import { TaskApprovalActions } from '@/components/layout/TaskApprovalActions';
import {
  usePendingApprovals,
  useInvalidatePendingApprovals,
} from '@/lib/task-approvals-store';

import { formatDataHoraSegundos } from '@/lib/format-date';
export function PendingApprovalsWidget() {
  const { data: approvals = [], isLoading, error } = usePendingApprovals();
  const invalidate = useInvalidatePendingApprovals();

  // Auto-hide quando vazio (sem pendência = sem widget)
  if (isLoading) return null;
  if (error) return null; // silenciosa (403 quando user não é admin/aprovador)
  if (approvals.length === 0) return null;

  return (
    <Card variant="flat" className="p-4" data-testid="pending-approvals-widget">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-semibold text-lg text-ink-1 dark:text-ink-dark-1">
          Aprovações pendentes
        </h3>
        <span className="text-xs text-ink-3 dark:text-ink-dark-3 tabular-nums">
          {approvals.length}
        </span>
      </div>
      <Alert variant="info" rail className="mb-3 text-xs">
        Decida aqui ou pela notificação no sino 🔔.
      </Alert>
      <ul className="space-y-3">
        {approvals.map((a) => (
          <li
            key={a.id}
            data-testid={`pending-approval-${a.id}`}
            className="border border-line dark:border-line-dark rounded-lg p-3"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 truncate">
                  {a.task.titulo}
                </p>
                <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5">
                  {a.solicitanteNome ?? a.solicitanteUserId} pediu aprovação
                </p>
                {a.task.startAtIso && (
                  <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5">
                    📅 {formatDataHoraSegundos(a.task.startAtIso)}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-2">
              <TaskApprovalActions approvalId={a.id} onDecided={invalidate} />
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
